import { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { validateBarcode } from '../utils/barcodeUtils';
import {
  playSuccessSound,
  triggerHapticFeedback,
  showFeedbackMessage,
} from '../utils/scannerFeedback';

import ScannerHeader from './scanner/ScannerHeader';
import ScannerModeTabs from './scanner/ScannerModeTabs';
import CameraView from './scanner/CameraView';
import ManualEntryForm from './scanner/ManualEntryForm';
import ScannerError from './scanner/ScannerError';

/**
 * Barcode scanner shell.
 * IMPORTANT: the camera lifecycle (start / stop / stale-closure refs / cleanup-on-unmount)
 * lives in THIS file deliberately — moving it out breaks the html5-qrcode mount cycle
 * (the "cameras scrolling down the page" bug). Only JSX was extracted to /components/scanner/*.
 * Feedback side-effects (audio / haptics / transient message) live in utils/scannerFeedback.js.
 */
export default function BarcodeScanner({ onScan, onClose, continuousMode = false }) {
  const [scannerMode, setScannerMode] = useState('camera'); // 'camera' | 'manual'
  const [manualInput, setManualInput] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState('');
  const [cameraPermission, setCameraPermission] = useState(null); // eslint-disable-line no-unused-vars
  const [feedbackMessage, setFeedbackMessage] = useState('');

  const html5QrCodeRef = useRef(null);

  // Refs for values used inside html5-qrcode callbacks (avoid stale closures)
  const isMountedRef = useRef(false);
  const isStartingRef = useRef(false);
  const scanEnabledRef = useRef(true);
  const continuousModeRef = useRef(continuousMode);

  // Keep continuousMode ref in sync
  useEffect(() => {
    continuousModeRef.current = continuousMode;
  }, [continuousMode]);

  // Initialize camera scanner ONCE per scannerMode change.
  // CRITICAL: do NOT include isScanning in deps — it causes an infinite mount/unmount loop
  // which stacks multiple Html5Qrcode instances and produces the "cameras scrolling down the page" bug.
  useEffect(() => {
    isMountedRef.current = true;
    if (scannerMode === 'camera') {
      startCameraScanner();
    }
    return () => {
      isMountedRef.current = false;
      stopCameraScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannerMode]);

  const startCameraScanner = async () => {
    // Guard: never allow two concurrent starts (second call is silently ignored)
    if (isStartingRef.current) {
      console.log('[Scanner] Start already in progress — ignoring duplicate call');
      return;
    }
    isStartingRef.current = true;

    try {
      setError('');

      // CRITICAL: purge any leftover <video>/<canvas> elements from a prior mount
      // before html5-qrcode appends fresh ones. Without this, rapid remounts stack them.
      const readerEl = document.getElementById('barcode-scanner-reader');
      if (readerEl) {
        readerEl.replaceChildren();
      }

      // Reset scan-enable guard for this session
      scanEnabledRef.current = true;

      // Initialize scanner
      const html5QrCode = new Html5Qrcode('barcode-scanner-reader');
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 150 },
          supportedScanTypes: [Html5Qrcode.SCAN_TYPE_CAMERA],
        },
        (decodedText) => {
          if (scanEnabledRef.current) {
            handleBarcodeDetected(decodedText);
          }
        },
        () => {
          // Per-frame non-detection error — ignore
        }
      );

      if (isMountedRef.current) {
        setIsScanning(true);
        setCameraPermission('granted');
        // NOTE: auto-torch REMOVED 20 Apr — too bright on modern iPhones, obscures
        // near-field barcodes. Smart low-light detection scheduled post-pitch (Phase 3d).
      } else {
        try {
          await html5QrCode.stop();
          html5QrCode.clear();
        } catch (e) {
          console.debug('[Scanner] Unmount-during-start cleanup error (safe):', e?.message);
        }
        html5QrCodeRef.current = null;
      }
    } catch (err) {
      console.error('[Scanner] Camera start error:', err);
      if (!isMountedRef.current) return;

      if (err.name === 'NotAllowedError' || err.message?.includes('Permission')) {
        setCameraPermission('denied');
        setError('Camera permission denied. Please allow camera access or use manual input.');
      } else if (err.message?.includes('NotFoundError')) {
        setError('No camera found. Please use manual input.');
      } else {
        setError('Failed to start camera. Please use manual input.');
      }

      setIsScanning(false);
      setScannerMode('manual');
    } finally {
      isStartingRef.current = false;
    }
  };

  const stopCameraScanner = async () => {
    const instance = html5QrCodeRef.current;
    html5QrCodeRef.current = null; // Drop the ref immediately so concurrent stops are no-ops

    if (instance) {
      try {
        if (instance.isScanning) {
          await instance.stop();
        }
        instance.clear();
      } catch (err) {
        console.log('[Scanner] Stop error (safe to ignore):', err?.message);
      }
    }

    const readerEl = document.getElementById('barcode-scanner-reader');
    if (readerEl) readerEl.replaceChildren();

    if (isMountedRef.current) {
      setIsScanning(false);
    }
  };

  const handleBarcodeDetected = async (barcode) => {
    if (!scanEnabledRef.current) return;
    scanEnabledRef.current = false;

    const validation = validateBarcode(barcode);

    if (validation.valid) {
      playSuccessSound();
      triggerHapticFeedback();
      showFeedbackMessage(setFeedbackMessage, '✓ Scanned!');

      const payload = {
        barcode: validation.formatted,
        type: validation.type,
        raw: barcode,
      };

      if (continuousModeRef.current) {
        onScan(payload);
        setTimeout(() => { scanEnabledRef.current = true; }, 1500);
      } else {
        await stopCameraScanner();
        setTimeout(() => { onScan(payload); onClose(); }, 300);
      }
    } else {
      await stopCameraScanner();
      if (!isMountedRef.current) return;
      setError(`Invalid barcode format: ${barcode}. Please try again or enter manually.`);
      setScannerMode('manual');
      setManualInput(barcode);
      scanEnabledRef.current = true;
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!manualInput.trim()) {
      setError('Please enter a barcode');
      return;
    }

    const validation = validateBarcode(manualInput.trim());
    if (validation.valid) {
      onScan({ barcode: validation.formatted, type: validation.type, raw: manualInput.trim() });
      onClose();
    } else {
      setError(`Invalid barcode: "${manualInput}". Must be EAN-13 (13 digits), ISBN-10 (10 digits), or UPC-A (12 digits).`);
    }
  };

  const switchToManual = async () => {
    await stopCameraScanner();
    setScannerMode('manual');
    setError('');
  };

  const switchToCamera = () => {
    setScannerMode('camera');
    setError('');
    setManualInput('');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <ScannerHeader
          scannerMode={scannerMode}
          continuousMode={continuousMode}
          onClose={onClose}
        />

        <ScannerModeTabs
          scannerMode={scannerMode}
          onSelectCamera={switchToCamera}
          onSelectManual={switchToManual}
        />

        <div className="p-4">
          {scannerMode === 'camera' ? (
            <CameraView isScanning={isScanning} feedbackMessage={feedbackMessage} />
          ) : (
            <ManualEntryForm
              manualInput={manualInput}
              setManualInput={setManualInput}
              onSubmit={handleManualSubmit}
            />
          )}

          <ScannerError error={error} />
        </div>
      </div>
    </div>
  );
}
